import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type {
  CartFunnelMinimumAgeItemBlockConfig,
  CartFunnelMinimumAgeItemBlockScenarioContent,
} from '@front-platform/content-builder';
import { cartFunnelMinimumAgeItemBlockResolveConfig } from '@front-platform/content-builder';
import { Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, switchMap, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { CartFunnelStoreSelector } from '@common/selectors/cart-funnel.selector';
import { CartFunnelStoreDispatcher } from '@common/dispatchers/cart-funnel.dispatcher';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { CartFunnelContinueService } from '@core/services/cart-funnel/cart-funnel-continue.service';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { TranslationField } from '@shared/interfaces/translation-field.interface';
import { TranslateFieldPipe } from '@shared/pipes/translate-field/translate-field.pipe';
import { isCartFunnelStepComplete } from '@store/cart-funnel';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';

export type MinimumAgeChoice = 'yes' | 'no';

export interface CartFunnelMinimumAgeItemBlockRuntimeConfig {
  block_id?: string;
  form_id?: string;
}

export type CartFunnelMinimumAgeItemBlockConfigInput = Partial<
  Omit<CartFunnelMinimumAgeItemBlockConfig, keyof CartFunnelMinimumAgeItemBlockRuntimeConfig> &
    CartFunnelMinimumAgeItemBlockRuntimeConfig
>;
export type CartFunnelMinimumAgeItemBlockResolvedConfig = Omit<
  CartFunnelMinimumAgeItemBlockConfig,
  keyof CartFunnelMinimumAgeItemBlockRuntimeConfig
> &
  Partial<CartFunnelMinimumAgeItemBlockRuntimeConfig>;
export interface CartFunnelMinimumAgeItemBlockContract extends ConfigComponentContract<
  CartFunnelMinimumAgeItemBlockConfigInput,
  CartFunnelMinimumAgeItemBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-funnel-minimum-age-item-block',
  templateUrl: './cart-funnel-minimum-age-item-block.component.html',
  standalone: true,
  imports: [TranslateFieldPipe, ContentBuilderColorPipe],
})
export class CartFunnelMinimumAgeItemBlockComponent
  implements OnInit, CartFunnelMinimumAgeItemBlockContract
{
  readonly config = input<CartFunnelMinimumAgeItemBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartFunnelMinimumAgeItemBlockConfig,
    CartFunnelMinimumAgeItemBlockRuntimeConfig
  >(cartFunnelMinimumAgeItemBlockResolveConfig, () => ({ config: this.config() }));

  private readonly cartFunnelSelector = inject(CartFunnelStoreSelector);
  private readonly cartFunnelDispatcher = inject(CartFunnelStoreDispatcher);
  private readonly cartFunnelContinue = inject(CartFunnelContinueService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly settingsSelector = inject(SettingsStoreSelector);

  readonly question = computed(
    () => this.resolvedConfig()['question'] as unknown as TranslationField | string | undefined,
  );
  readonly yesLabel = computed(
    () => this.resolvedConfig()['yes_label'] as unknown as TranslationField | string | undefined,
  );
  readonly noLabel = computed(
    () => this.resolvedConfig()['no_label'] as unknown as TranslationField | string | undefined,
  );
  readonly denialMessage = computed(
    () =>
      this.resolvedConfig()['denial_message'] as unknown as TranslationField | string | undefined,
  );
  readonly minimumAge = computed(() => this.wholeNumber('minimum_age', 18, 0));
  readonly buttonFontSize = computed(() => {
    const configured = this.wholeNumber('button_font_size', 48, 12);
    if (this.isKiosk()) {
      return configured;
    }
    return Math.max(14, Math.round((configured * 16) / 48));
  });
  readonly buttonRadius = computed(() => this.wholeNumber('button_radius', 16, 0));
  readonly buttonMinHeight = computed(() => {
    const fontSize = this.buttonFontSize();
    return this.isKiosk() ? Math.max(256, fontSize * 2.4) : Math.max(56, Math.round(fontSize * 2.75));
  });
  readonly minimumAgeLabel = computed(() => String(this.minimumAge()));
  readonly blockId = computed(() => this.resolvedConfig()['block_id'] as unknown as string);
  readonly formId = computed(() => this.resolvedConfig()['form_id'] as unknown as string);
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined,
  );
  readonly choiceTextColor = computed(
    () =>
      this.resolvedConfig()['choice_text_color'] as unknown as
        IContentBuilderFieldColor | undefined,
  );
  readonly choiceBackgroundColor = computed(
    () =>
      this.resolvedConfig()['choice_background_color'] as unknown as
        IContentBuilderFieldColor | undefined,
  );
  readonly selectedTextColor = computed(
    () =>
      this.resolvedConfig()['selected_text_color'] as unknown as
        IContentBuilderFieldColor | undefined,
  );
  readonly selectedBackgroundColor = computed(
    () =>
      this.resolvedConfig()['selected_background_color'] as unknown as
        IContentBuilderFieldColor | undefined,
  );
  readonly messageColor = computed(
    () =>
      this.resolvedConfig()['message_color'] as unknown as IContentBuilderFieldColor | undefined,
  );
  readonly choiceBorderColor = computed(
    () =>
      this.resolvedConfig()['choice_border_color'] as unknown as
        IContentBuilderFieldColor | undefined,
  );

  private readonly previewScenario$ = previewScenarioForBloc$(
    'CartFunnelMinimumAgeItemBlockComponent',
  );
  protected readonly isKiosk = this.settingsSelector.isKioskLayout;
  protected readonly choice = signal<MinimumAgeChoice | null>(null);
  protected readonly questionId = computed(() => `${this.blockId() || 'minimum-age'}-question`);
  protected readonly messageId = computed(() => `${this.blockId() || 'minimum-age'}-message`);
  protected readonly translationParams = computed(() => ({
    minimumAge: this.minimumAgeLabel(),
  }));

  ngOnInit(): void {
    const blockId = this.blockId();
    if (!blockId) {
      console.error('[CartFunnelMinimumAgeItemBlockComponent] blockId is missing');
      return;
    }

    this.cartFunnelDispatcher.addValue({
      id: blockId,
      title: this.resolvedText(this.question(), 'cart-funnel.minimum-age.question'),
      displayTitle: true,
      formId: this.formId() || blockId,
      required: true,
    });

    this.cartFunnelSelector
      .selectValue(blockId)
      .pipe(take(1))
      .subscribe(form => {
        if (form?.value === false) {
          this.choice.set('no');
          return;
        }
        if (typeof form?.value === 'string' && form.value) {
          this.choice.set('yes');
        }
      });

    this.previewScenario$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(scenario => {
      const content = previewScenarioData<CartFunnelMinimumAgeItemBlockScenarioContent>(
        scenario,
        'CartFunnelMinimumAgeItemBlockComponent',
      );
      if (!content) {
        return;
      }
      this.writeAnswer(content.answer === 'no' ? 'no' : null);
    });
  }

  choose(answer: MinimumAgeChoice): void {
    this.writeAnswer(answer);
    if (answer === 'yes') {
      this.advanceWhenStepIsComplete();
    }
  }

  private writeAnswer(answer: MinimumAgeChoice | null): void {
    const blockId = this.blockId();
    if (!blockId) {
      return;
    }

    this.choice.set(answer);
    this.cartFunnelDispatcher.updateValue({
      id: blockId,
      changes: {
        title: this.resolvedText(this.question(), 'cart-funnel.minimum-age.question'),
        displayTitle: true,
        required: true,
        value:
          answer === 'yes'
            ? this.resolvedText(this.yesLabel(), 'cart-funnel.minimum-age.yes')
            : answer === 'no'
              ? false
              : '',
      },
    });
  }

  private advanceWhenStepIsComplete(): void {
    this.cartFunnelSelector.selectActiveStep
      .pipe(
        take(1),
        switchMap(active =>
          this.cartFunnelSelector.selectValues.pipe(
            take(1),
            map(values => values.filter(value => value.formId === active?.id)),
          ),
        ),
      )
      .subscribe(stepValues => {
        if (stepValues.length > 0 && isCartFunnelStepComplete(stepValues)) {
          this.cartFunnelContinue.request();
        }
      });
  }

  private wholeNumber(key: string, fallback: number, minimum: number): number {
    const value = Number(this.resolvedConfig()[key]);
    if (!Number.isFinite(value)) {
      return fallback;
    }
    return Math.max(minimum, Math.floor(value));
  }

  private resolvedText(field: TranslationField | string | undefined, fallbackKey: string): string {
    const minimumAge = this.minimumAgeLabel();
    const custom =
      typeof field === 'string' ? field.trim() : field?.[this.translateService.currentLang]?.trim();
    if (custom) {
      return custom.replace(/\{\{\s*minimumAge\s*\}\}/g, minimumAge);
    }
    return this.translateService.instant(fallbackKey, { minimumAge });
  }
}

COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartFunnelMinimumAgeItemBlockComponent, {
  keyAliases: {},
});
